/**
 * Classic (flip-card) study page.
 * Routes:
 *   /decks/:deckId/study           — authenticated or not
 *   /shared/deck/:token/study      — anonymous, via share_token
 *
 * Keyboard shortcuts: Space = flip, ArrowRight = know, ArrowLeft = don't know
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowLeftRight, List, Settings, X } from 'lucide-react'

import StudyComplete from '../components/study/StudyComplete'
import StudyProgress from '../components/study/StudyProgress'
import { useLocale } from '../i18n/context'
import {
  useClassicStudyBuffer,
  useCompleteClassicSession,
  useCreateClassicSession,
  useDeckDisplayPreference,
  useDeckKinds,
  useToggleClassicShuffle,
  useUpsertDeckDisplayPreference,
} from '../hooks/useClassicStudy'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import type { ClassicCardSide, ClassicNextCard, ClassicSessionProgress } from '../types'
import { resolveCollapsedParts } from '../utils/collapsedParts'
import { findTermSide, isTermSide } from '../utils/cardSides'
import { sideKindLabel } from '../utils/cardSideDisplay'
import { buildFillInBlank, type FibSegment } from '../utils/learningUtils'
import { isBlank } from '../utils/fieldValue'

// ── Types ─────────────────────────────────────────────────────────────────────

// 'picking-direction' kept in type for potential future use, but never entered
type Phase = 'picking-direction' | 'loading' | 'studying' | 'done'
type SideFirst = 'term' | 'definition'

// ── Image-aware content renderer ──────────────────────────────────────────────

const IMAGE_LABELS = new Set(['Image', 'Image URL'])
const AUDIO_LABELS = new Set(['Audio', 'Audio URL'])
const IMAGE_URL_RE = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|avif)(\?.*)?$/i
const AUDIO_URL_RE = /^https?:\/\/.+\.(mp3|m4a|ogg|wav|webm|aac|opus|flac)(\?.*)?$/i

function isImageSide(label: string, content: string): boolean {
  return IMAGE_LABELS.has(label) || content.startsWith('data:image/') || IMAGE_URL_RE.test(content.trim())
}

function isAudioSide(label: string, content: string): boolean {
  return AUDIO_LABELS.has(label) || content.startsWith('data:audio/') || AUDIO_URL_RE.test(content.trim())
}

/** True only when content is an actual audio src (URL or data URI), not plain text. */
function isValidAudioSrc(content: string): boolean {
  return content.startsWith('data:audio/') || AUDIO_URL_RE.test(content.trim())
}

// ── Audio playback management ─────────────────────────────────────────────────
// Ensures only one <audio> plays at a time across card faces and the overlay,
// and provides a single function to stop playback on card navigation.
let activeAudio: HTMLAudioElement | null = null
function stopActiveAudio() { activeAudio?.pause() }
function handleAudioPlay(e: React.SyntheticEvent<HTMLAudioElement>) {
  const el = e.currentTarget
  if (activeAudio && activeAudio !== el) activeAudio.pause()
  activeAudio = el
}

// ── Swipe-to-grade constants ─────────────────────────────────────────────────
const SWIPE_COMMIT_PX = 72    // horizontal distance to commit a grade (~18% of a 390px screen; tint is fully opaque at this point)
const SWIPE_COMMIT_VEL = 0.45 // px/ms velocity shortcut for a fast flick
const SWIPE_LOCK_PX = 10      // horizontal travel before we "own" the gesture
const IOS_EDGE_PX = 20        // left-edge guard for iOS system back gesture
const FLYOFF_MS = 320         // swipe fly-off duration before the buffered card is shown (matches the 0.3s wrapper transition)

function isExampleSide(side: ClassicCardSide): boolean {
  return side.kind === 'example' || (!side.kind && side.label === 'Example')
}

/**
 * Drops Example sides that cannot be blanked while pre-flip protection is
 * active (ADR-068 Decision 7).
 *
 * `blankTerm` is non-null only on the definition-first *question* face, before
 * the flip — the face whose whole job is to withhold the term. An Example side
 * there is shown blanked. Since ADR-068 Decision 6 retired the prefix fallback,
 * `buildFillInBlank` now returns null for any example that does not contain the
 * term as a whole word, and rendering such a side would print the sentence
 * un-blanked on the question face — printing "I love dogs" while asking the
 * learner to produce "Dog". If the answer cannot be hidden, the example is not
 * shown on that face at all.
 *
 * Filtering happens *before* collapsed-part resolution so a card carrying both a
 * definition and an unblankable example still shows its definition rather than
 * collapsing to nothing. Faces rendered after the answer is known pass
 * `blankTerm == null` and are returned untouched.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for classicBlankProtection.test.ts
export function visibleUnderBlankProtection(
  parts: ClassicCardSide[],
  blankTerm?: string | null,
): ClassicCardSide[] {
  if (!blankTerm) return parts
  return parts.filter((p) => !isExampleSide(p) || buildFillInBlank(p.content, blankTerm) !== null)
}

/** Renders an example sentence with the term blanked as an underline placeholder. */
function renderBlanked(segments: FibSegment[], fontSize: number, blankLabel: string): React.ReactNode {
  return (
    <span style={{ fontSize, fontFamily: 'var(--font-card)', color: 'var(--text-primary)', lineHeight: 1.35, whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {segments.map((seg, i) =>
        seg.kind === 'text' ? (
          <span key={i}>{seg.text}</span>
        ) : (
          <span
            key={i}
            aria-label={blankLabel}
            style={{
              display: 'inline-block',
              width: Math.max(48, Math.min(200, seg.match.length * 13)),
              height: '1em',
              borderBottom: '2.5px solid var(--text-primary)',
              marginInline: 4,
              verticalAlign: 'text-bottom',
              opacity: 0.35,
            }}
          />
        )
      )}
    </span>
  )
}

function renderContent(label: string, content: string) {
  if (isImageSide(label, content)) {
    return (
      <img
        src={content}
        alt=""
        style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'contain' }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  if (isAudioSide(label, content)) {
    // Invalid legacy content (plain text in an Audio-labelled side): render as text.
    if (!isValidAudioSrc(content)) return <>{content}</>
    return (
      <audio
        src={content}
        controls
        preload="metadata"
        onPlay={handleAudioPlay}
        onClick={(e) => e.stopPropagation()}
        // touchAction:auto overrides the page-level none so native controls work on iOS/TWA.
        style={{ maxWidth: '100%', width: 280, touchAction: 'auto' }}
      />
    )
  }
  return <>{content}</>
}

// ── Side resolution ───────────────────────────────────────────────────────────
//
// Two logical sides (ADR-037 / ux_scenario §10):
//   Side A = the Term, and ONLY the Term.
//   Side B = every other part (Definition, Example, Image, custom…) in the
//            card's existing part order. Its PRIMARY part is the first one.
// The front-side toggle (sideFirst) only chooses which side faces front.
//
// API contract: front_side = the deck's prompt side, resolved structurally by the
// backend from default_prompt_side (a Side-B kind, e.g. "definition" — ADR-040
// Step 4a), never the Term; back_sides = [Term, Example, ...]. Face identity here
// is kind-based via isTermSide/findTermSide, so this consumes the structural
// representation directly — no label string is matched.

type FaceKind = 'term' | 'group'

interface ResolvedFace {
  kind: FaceKind
  parts: ClassicCardSide[]
}

interface ResolvedSides {
  front: ResolvedFace
  back: ResolvedFace
  groupParts: ClassicCardSide[] // the full Side B stack — fed to the "Show all" overlay
}

// ── ADR-104 Decision 7: every card can be studied, and no face is blank ─────
//
// A card may lack a term (reachable only through the API) or everything but
// its term (a word-only card, or one whose Side-B fields are all blank). Each
// face is built from FILLED sides only; a face with nothing to show states
// what is missing instead of rendering an empty card. The Term is never a
// Side-B part: for a card with nothing on Side B the server still names the
// Term as `front_side` (a required field), and it is recognised here by kind.

/** The card's Term side, wherever the payload put it. */
function termSideOf(card: ClassicNextCard): ClassicCardSide | undefined {
  return findTermSide(card.back_sides) ?? (isTermSide(card.front_side) ? card.front_side : undefined)
}

/** The Term's text when it is filled, else null (nothing to show or hide). */
// eslint-disable-next-line react-refresh/only-export-components -- exported for adr104Classic.test.tsx
export function filledTermText(card: ClassicNextCard): string | null {
  const term = termSideOf(card)
  return term && !isBlank(term.content) ? term.content : null
}

/** Every Side-B part as stored, blank ones included, never the Term: what the
 * "Show all" overlay lists (ADR-104 Decision 4). */
function allSideBParts(card: ClassicNextCard): ClassicCardSide[] {
  return [card.front_side, ...card.back_sides].filter((s) => !isTermSide(s))
}

/** Side-B parts a face can show: filled, never the Term. */
// eslint-disable-next-line react-refresh/only-export-components -- exported for adr104Classic.test.tsx
export function filledSideBParts(card: ClassicNextCard): ClassicCardSide[] {
  return allSideBParts(card).filter((s) => !isBlank(s.content))
}

/**
 * The direction THIS card is shown in (ADR-104 Decision 7). The reader's
 * direction decides, unless the face it puts in front has nothing it can show
 * there while the other face does; then the other face is in front, for this
 * card only. The stored direction is never changed, and the next card uses it
 * again. On the front, before the flip, an Example that cannot be blanked is
 * withheld (ADR-068 Decision 7), so it does not count as something to show.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for adr104Classic.test.tsx
export function effectiveSideFirst(card: ClassicNextCard, sideFirst: SideFirst): SideFirst {
  const term = filledTermText(card)
  const sideB = filledSideBParts(card)
  if (sideFirst === 'definition') {
    const sideBShowableOnFront = visibleUnderBlankProtection(sideB, term).length > 0
    return !sideBShowableOnFront && term !== null ? 'term' : 'definition'
  }
  return term === null && sideB.length > 0 ? 'definition' : 'term'
}

function resolveDisplaySides(card: ClassicNextCard, sideFirst: SideFirst): ResolvedSides {
  const termSide = termSideOf(card)
  // Side B: the prompt side first (card.front_side), then every other non-Term
  // part in the card's existing order, filled parts only. Primary = parts[0].
  const termFace: ResolvedFace = {
    kind: 'term',
    parts: termSide && !isBlank(termSide.content) ? [termSide] : [],
  }
  const groupFace: ResolvedFace = { kind: 'group', parts: filledSideBParts(card) }
  const groupParts = allSideBParts(card)

  // sideFirst === 'definition' → Side B is the prompt (front); Term revealed on flip.
  // sideFirst === 'term'       → Side A (Term) is the prompt; Side B revealed on flip.
  if (sideFirst === 'definition') {
    return { front: groupFace, back: termFace, groupParts }
  }
  return { front: termFace, back: groupFace, groupParts }
}

// ── Card face — renders ONE logical side ──────────────────────────────────────
//
// ADR-037: the collapsed card shows a single short side and NEVER scrolls.
//   - Side A (term):  the term, centered.
//   - Side B (group): only its primary (first) part, plus a labelled, full-width
//     "Show all" control that opens the scrollable overlay with every part.
// There is NO inner scroll container here — that is the centre-grab fix (ADR-037
// update 2026-06-20): an overflow:auto subscroller steals a centre-start swipe
// even when its content does not overflow. A long primary part is line-clamped
// and read in full via the overlay; long stacks live entirely in the overlay.

function CardFaceSides({
  face,
  isBack,
  showTap,
  onShowAll,
  collapsedParts,
  blankTerm,
}: {
  face: ResolvedFace
  isBack: boolean
  showTap?: boolean
  onShowAll?: () => void
  collapsedParts?: string[] | null
  /** When set, blank the term in Example sides (pre-flip protection). */
  blankTerm?: string | null
}) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const ce = tr.cardEditor
  const isGroup = face.kind === 'group'

  // Under pre-flip protection, an Example side that cannot be blanked is not a
  // candidate for display at all (ADR-068 Decision 7). Applied before collapsed-
  // part resolution so the preference picks from what may actually be shown.
  const eligibleParts = visibleUnderBlankProtection(face.parts, blankTerm)

  // Which parts to render in the collapsed view (ADR-039).
  // For the term face this is always just the term; for the group face it
  // follows the user's stored preference, falling back to the first part.
  const displayParts: ClassicCardSide[] = isGroup
    ? resolveCollapsedParts(eligibleParts, collapsedParts ?? null)
    : eligibleParts.slice(0, 1)

  const primary = displayParts[0]
  const twoPartMode = isGroup && displayParts.length === 2
  // Single image: fills available height via flex rather than hard-clipping
  const isSingleImage = displayParts.length === 1 && isImageSide(primary?.label ?? '', primary?.content ?? '')
  // More Side-B parts exist beyond what's shown collapsed → offer "Show all".
  // Counted against eligibleParts, not face.parts: a part withheld by blank
  // protection must not make "Show all" appear promising content the overlay
  // will (correctly) also withhold.
  const hasMore = isGroup && eligibleParts.length > displayParts.length

  // Detect overflow on the first text part (skipped in two-part mode — we
  // always offer "Show all" there, and stack height makes ref check unreliable).
  const primaryRef = useRef<HTMLDivElement>(null)
  const [truncated, setTruncated] = useState(false)
  useLayoutEffect(() => {
    if (twoPartMode) return
    const el = primaryRef.current
    if (!el) return
    setTruncated(el.scrollHeight > el.clientHeight + 1)
  }, [primary?.id, primary?.content, twoPartMode])

  const showAll = isGroup && (twoPartMode || hasMore || truncated)
  // Soft bottom fade: signals content is clipped below (ADR-039)
  const showFade = isGroup && (twoPartMode || truncated)

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backfaceVisibility: 'hidden',
        transform: isBack ? 'rotateY(180deg)' : undefined,
      }}
    >
      {!isBack && <BracketCorners />}
      <div
        style={{
          width: '100%',
          height: '100%',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-flashcard)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 32px',
          // Visual clip for the rounded corners only — NOT a scroll container.
          overflow: 'hidden',
        }}
      >
        {/* Content area — fixed height via flex:1 min-height:0; never scrolls */}
        <div
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Top-align when stacking 2 parts or a single image (fills height);
            // center for single text parts (existing wave-1 look).
            justifyContent: (twoPartMode || isSingleImage) ? 'flex-start' : 'center',
            // Clips content at the boundary so no part overflows the card.
            // This is safe here (parent already has overflow:hidden for border-radius)
            // but makes the fade's bottom edge land correctly in the flex child.
            overflow: 'hidden',
            position: 'relative', // anchor for the soft fade overlay
            paddingTop: twoPartMode ? 4 : 0,
            gap: twoPartMode ? 10 : 0,
          }}
        >
          {displayParts.length === 0 ? (
            // ADR-104 Decision 7: this face has nothing to show. Say which side
            // is empty, in secondary UI type, so it cannot be read as the
            // card's content. Nothing from a dictionary stands in for it.
            <div
              data-testid="classic-empty-face"
              style={{ fontSize: 15, fontFamily: 'var(--font-ui)', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.4, maxWidth: 280 }}
            >
              {isGroup ? cs.emptySideBFace : cs.emptyTermFace}
            </div>
          ) : isSingleImage ? (
            // Single image scales to the full available height via flex (ADR-039).
            // flex:1 + min-height:0 lets the img grow to fill the content area;
            // object-fit:contain keeps aspect ratio without hard-clipping pixels.
            <img
              src={primary.content}
              alt=""
              style={{
                flex: 1,
                minHeight: 0,
                maxWidth: '100%',
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            displayParts.map((p, idx) => (
              <div key={p.id} style={{ textAlign: 'center', width: '100%', flexShrink: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  {sideKindLabel(p, ce)}
                </div>
                {isImageSide(p.label, p.content) ? (
                  // Image in 2-part mode: capped at 100px so the second part
                  // still fits within the collapsed card height.
                  <img
                    src={p.content}
                    alt=""
                    style={{ maxWidth: '100%', maxHeight: 100, objectFit: 'contain', borderRadius: 6, display: 'block', margin: '0 auto' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                ) : isAudioSide(p.label, p.content) ? (
                  isValidAudioSrc(p.content) ? (
                    <audio
                      src={p.content}
                      controls
                      preload="metadata"
                      onPlay={handleAudioPlay}
                      onClick={(e) => e.stopPropagation()}
                      style={{ maxWidth: '100%', width: 260, touchAction: 'auto' }}
                    />
                  ) : (
                    <div style={{ fontSize: 14, fontFamily: 'var(--font-card)', color: 'var(--text-primary)', lineHeight: 1.35 }}>
                      {p.content}
                    </div>
                  )
                ) : (() => {
                  const fontSize = twoPartMode ? 20 : 26
                  // Blank the term inside Example sides when pre-flip protection is active.
                  const fibSegments = blankTerm && isExampleSide(p)
                    ? buildFillInBlank(p.content, blankTerm)
                    : null
                  return (
                    <div
                      ref={idx === 0 ? primaryRef : undefined}
                      style={{
                        fontSize,
                        fontFamily: 'var(--font-card)',
                        color: 'var(--text-primary)',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: twoPartMode ? 3 : 6,
                        overflow: 'hidden',
                      }}
                    >
                      {fibSegments ? renderBlanked(fibSegments, fontSize, tr.learn.blankPlaceholder) : p.content}
                    </div>
                  )
                })()}
              </div>
            ))
          )}

          {/* Soft fade — signals content continues below the clip boundary.
              Uses --bg-surface (the card background) for seamless blending. */}
          {showFade && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 40,
                background: 'linear-gradient(to bottom, transparent, var(--bg-surface))',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        {/* "Show all" — labelled, full-width affordance at the bottom of Side B */}
        {showAll && (
          <button
            onClick={(e) => { e.stopPropagation(); onShowAll?.() }}
            style={{
              flexShrink: 0,
              width: '100%',
              marginTop: 12,
              padding: '10px 16px',
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-secondary)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: 44,
            }}
          >
            <List size={15} />
            {cs.showAll}
          </button>
        )}

        {showTap && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, flexShrink: 0 }}>
            {cs.tapToFlip}
          </div>
        )}
      </div>
    </div>
  )
}

// ── "Show all" overlay — the ONLY place long content scrolls (ADR-037) ─────────

// ── Display settings panel (ADR-039/040) ─────────────────────────────────────
//
// Proper sheet: pinned header + pinned Save + scrollable body. Kind list comes
// from GET /{id}/kinds/ — the deck's actual Side-B kinds with per-kind coverage.
// Selection: tap-to-number (first tap = slot 1, next = slot 2, 3rd prevented;
// clearing slot 1 promotes slot 2 automatically). No separate ▲▼ order block.
// Live mini-preview shows the effect of the current selection on the current card.
// Reconciliation: stored kinds absent from the deck are silently dropped on open.
// Only rendered for authenticated deck pages (deckId present).

interface DisplaySettingsPanelProps {
  deckId: string
  currentParts: string[] | null
  currentCard: ClassicNextCard | null
  saving: boolean
  onSave: (parts: string[] | null) => void
  onClose: () => void
}

// Mini card preview — shows which parts the collapsed card would display.
function MiniPreview({ parts, hasCard }: { parts: ClassicCardSide[]; hasCard: boolean }) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const ce = tr.cardEditor
  const twoPartMode = parts.length === 2
  const noSelection = parts.length === 0

  return (
    <div
      style={{
        borderRadius: 10,
        border: '1.5px solid var(--border)',
        background: 'var(--bg-elevated)',
        padding: '12px 14px',
        minHeight: 72,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {noSelection || !hasCard ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary)',
            fontSize: 12,
            fontFamily: 'var(--font-ui)',
            fontStyle: 'italic',
          }}
        >
          {noSelection ? cs.autoFirst : cs.noCard}
        </div>
      ) : (
        <>
          {parts.map((p, idx) => (
            <div
              key={p.id}
              style={{
                borderTop: idx > 0 ? '1px solid var(--border)' : 'none',
                paddingTop: idx > 0 ? 6 : 0,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 2,
                  fontFamily: 'var(--font-ui)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sideKindLabel(p, ce)}
              </div>
              {isImageSide(p.label, p.content) ? (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                  {cs.imageCaption}
                </div>
              ) : isAudioSide(p.label, p.content) ? (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                  {cs.audioCaption}
                </div>
              ) : (
                <div
                  style={{
                    fontSize: twoPartMode ? 12 : 14,
                    fontFamily: 'var(--font-card)',
                    color: 'var(--text-primary)',
                    lineHeight: 1.35,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: twoPartMode ? 2 : 3,
                    overflow: 'hidden',
                  }}
                >
                  {p.content}
                </div>
              )}
            </div>
          ))}
          {/* Soft fade to signal clipped content */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 24,
              background: 'linear-gradient(to bottom, transparent, var(--bg-elevated))',
              pointerEvents: 'none',
            }}
          />
        </>
      )}
    </div>
  )
}

function DisplaySettingsPanel({
  deckId,
  currentParts,
  currentCard,
  saving,
  onSave,
  onClose,
}: DisplaySettingsPanelProps) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const ce = tr.cardEditor
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'classic-display-settings')
  const { data: kindsData, isLoading: kindsLoading, isError: kindsError } = useDeckKinds(deckId)
  const availableKinds = kindsData?.kinds ?? []

  // Reconciled baseline: stored prefs filtered to kinds actually present on deck.
  // Initialise immediately if kinds are already cached; reconcile on first load otherwise.
  const reconciledBaseline = useMemo(() => {
    if (!kindsData) return currentParts ?? []
    const avail = new Set(kindsData.kinds.map(k => k.kind))
    return (currentParts ?? []).filter(k => avail.has(k))
  }, [kindsData, currentParts])

  const [selected, setSelected] = useState<string[]>(() => {
    // Use cached kinds for immediate reconciliation if available.
    if (kindsData) {
      const avail = new Set(kindsData.kinds.map(k => k.kind))
      return (currentParts ?? []).filter(k => avail.has(k))
    }
    return currentParts ?? []
  })

  // Reconcile on first kinds load (runs once; never clobbers user's live edits).
  const didReconcileRef = useRef(!!kindsData)
  useEffect(() => {
    if (!kindsData || didReconcileRef.current) return
    didReconcileRef.current = true
    const avail = new Set(kindsData.kinds.map(k => k.kind))
    setSelected(prev => prev.filter(k => avail.has(k)))
  }, [kindsData])

  // Tap-to-number: first tap = slot 1, second different kind = slot 2.
  // Clearing slot 1 automatically promotes slot 2 (filter preserves order).
  // Third selection is silently prevented.
  const toggle = (kind: string) => {
    setSelected(prev => {
      if (prev.includes(kind)) return prev.filter(k => k !== kind)
      if (prev.length >= 2) return prev
      return [...prev, kind]
    })
  }

  const isDirty = JSON.stringify(selected) !== JSON.stringify(reconciledBaseline)

  // Live preview: resolve which parts the current card would show.
  const previewGroupParts = useMemo(
    () => (currentCard ? filledSideBParts(currentCard) : []),
    [currentCard],
  )

  const previewDisplayParts = useMemo(
    () => resolveCollapsedParts(previewGroupParts, selected.length > 0 ? selected : null),
    [previewGroupParts, selected],
  )

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal-backdrop)' as never,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        touchAction: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={cs.cardDisplay}
        style={{
          width: '100%',
          maxWidth: 420,
          // Clamp to viewport height so header and Save are always reachable.
          maxHeight: 'calc(100vh - 48px)',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-flashcard)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Pinned header ─────────────────────────────────────────── */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 12px 12px 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
            {cs.cardDisplay}
          </span>
          <button
            onClick={onClose}
            aria-label={cs.close}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              minHeight: 44, minWidth: 44,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ───────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            // Explicit auto so the panel scrolls on Android even though the
            // page root has touchAction:none (touch-action is not inherited).
            touchAction: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* Kind picker */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 4, fontFamily: 'var(--font-ui)' }}>
              {cs.sideBParts}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
              {cs.sideBHint}
            </div>

            {kindsLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'var(--font-ui)', padding: '8px 0' }}>
                {cs.loading}
              </div>
            ) : kindsError ? (
              <div style={{ color: 'var(--danger)', fontSize: 13, fontFamily: 'var(--font-ui)', padding: '8px 0' }}>
                {cs.couldNotLoadParts}
              </div>
            ) : availableKinds.length === 0 ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'var(--font-ui)', padding: '8px 0', fontStyle: 'italic' }}>
                {cs.noParts}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {availableKinds.map(({ kind, fraction }) => {
                  const slotIdx = selected.indexOf(kind)
                  const isSelected = slotIdx !== -1
                  const isDisabled = !isSelected && selected.length >= 2

                  return (
                    <button
                      key={kind}
                      onClick={() => !isDisabled && toggle(kind)}
                      aria-pressed={isSelected}
                      aria-label={`${sideKindLabel({ kind, label: kind }, ce)}${isSelected ? `, slot ${slotIdx + 1}` : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: `1.5px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                        background: isSelected ? 'var(--accent-soft, rgba(99,102,241,0.08))' : 'var(--bg-elevated)',
                        opacity: isDisabled ? 0.38 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        width: '100%',
                        textAlign: 'left',
                        transition: 'border-color 0.15s, background 0.15s',
                        minHeight: 44,
                      }}
                    >
                      {/* Slot badge: filled circle with number when selected, empty ring otherwise */}
                      <div
                        style={{
                          flexShrink: 0,
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          border: `2px solid ${isSelected ? 'var(--accent-fill)' : 'var(--border)'}`,
                          background: isSelected ? 'var(--accent-fill)' : 'transparent',
                          color: isSelected ? 'var(--text-on-accent)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: 'var(--font-ui)',
                          transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                          userSelect: 'none',
                        }}
                      >
                        {isSelected ? slotIdx + 1 : ''}
                      </div>

                      {/* Kind name — truncate at max 50 chars (cosmetic label limit) */}
                      <span
                        style={{
                          flex: 1,
                          fontSize: 14,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
                          fontFamily: 'var(--font-ui)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sideKindLabel({ kind, label: kind }, ce)}
                      </span>

                      {/* Coverage indicator — only on heterogeneous decks (fraction < 1) */}
                      {fraction < 1.0 && (
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-secondary)',
                            background: 'var(--bg-page)',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            padding: '2px 6px',
                            fontFamily: 'var(--font-ui)',
                          }}
                          title={cs.presentOnPercent(Math.round(fraction * 100))}
                        >
                          {Math.round(fraction * 100)}%
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Live preview */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 8, fontFamily: 'var(--font-ui)' }}>
              {cs.preview}
            </div>
            <MiniPreview parts={previewDisplayParts} hasCard={!!currentCard} />
          </div>
        </div>

        {/* ── Pinned Save ───────────────────────────────────────────── */}
        <div
          style={{
            flexShrink: 0,
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
          }}
        >
          <button
            onClick={() => onSave(selected.length > 0 ? selected : null)}
            disabled={saving || !isDirty}
            style={{
              width: '100%',
              padding: '13px 20px',
              borderRadius: 10,
              border: 'none',
              background: (saving || !isDirty) ? 'var(--bg-elevated)' : 'var(--accent-fill)',
              color: (saving || !isDirty) ? 'var(--text-secondary)' : 'var(--text-on-accent)',
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'var(--font-ui)',
              cursor: (saving || !isDirty) ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s, color 0.2s',
              minHeight: 48,
            }}
          >
            {saving ? cs.saving : isDirty ? cs.save : cs.saved}
          </button>
        </div>
      </div>
    </div>
  )
}

function ShowAllOverlay({ parts, onClose, blankTerm }: { parts: ClassicCardSide[]; onClose: () => void; blankTerm?: string | null }) {
  const { tr } = useLocale()
  const ce = tr.cardEditor
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'classic-show-all-overlay')
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal-backdrop)' as never,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={tr.classicStudy.allDetails}
        style={{
          width: '100%',
          maxWidth: 640,
          maxHeight: '80vh',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-flashcard)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px 12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>
            {tr.classicStudy.allDetails}
          </span>
          <button
            onClick={onClose}
            aria-label={tr.classicStudy.close}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* The single scroll container in Classic study — long content scrolls here only */}
        <div style={{ overflowY: 'auto', padding: '8px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Same withholding rule as the collapsed face (ADR-068 Decision 7):
              this overlay is reachable from the pre-flip question face, so an
              Example side that cannot be blanked is dropped rather than printed
              un-blanked. Post-flip the overlay passes blankTerm == null and
              shows everything, unchanged. */}
          {visibleUnderBlankProtection(parts, blankTerm).map((p) => {
            const fibSegments = blankTerm && isExampleSide(p)
              ? buildFillInBlank(p.content, blankTerm)
              : null
            return (
              <div key={p.id} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  {sideKindLabel(p, ce)}
                </div>
                <div style={{ fontSize: 18, fontFamily: 'var(--font-card)', color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {fibSegments ? renderBlanked(fibSegments, 18, tr.learn.blankPlaceholder) : renderContent(p.label, p.content)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Flip card ─────────────────────────────────────────────────────────────────
//
// Receives card + sideFirst and resolves sides internally so there is no nullable
// "resolved" prop flowing from the parent — eliminates the null/undefined prop
// crash that happened when concurrent React evaluated the JSX prop expression
// before the `&&` guard had narrowed the external `resolved` binding.

interface FlipCardProps {
  card: ClassicNextCard
  sideFirst: SideFirst
  flipped: boolean
  onFlip: () => void
  onShowAll: () => void
  collapsedParts: string[] | null
  /** Term text to blank in the front face's Example side (pre-flip only). */
  blankTerm?: string | null
}

function FlipCard({ card, sideFirst, flipped, onFlip, onShowAll, collapsedParts, blankTerm }: FlipCardProps) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const { front, back } = resolveDisplaySides(card, sideFirst)
  const prefersReducedMotion = useReducedMotion()
  // On every keyed remount (new card after grade) suppress the Framer Motion
  // transition for the mount cycle so the card always appears instantly at its
  // front face (rotateY 0), with no entrance animation and no MotionValue
  // inheritance from the previous card. useEffect fires after the first paint,
  // after which user-initiated flips animate normally.
  const hasMounted = useRef(false)
  useEffect(() => { hasMounted.current = true }, [])

  return (
    <div
      onClick={onFlip}
      style={{
        width: '100%',
        maxWidth: 640,
        height: 'clamp(280px, 50vh, 400px)',
        cursor: 'pointer',
        perspective: 1200,
        userSelect: 'none',
      }}
      aria-label={flipped ? cs.flipAriaBack : cs.flipAriaFront}
    >
      <motion.div
        initial={{ rotateY: 0 }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={
          !hasMounted.current || prefersReducedMotion
            ? { duration: 0 }
            : { duration: 0.45, ease: [0.4, 0, 0.2, 1] }
        }
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
        }}
      >
        <CardFaceSides face={front} isBack={false} showTap onShowAll={onShowAll} collapsedParts={collapsedParts} blankTerm={blankTerm} />
        <CardFaceSides face={back} isBack onShowAll={onShowAll} collapsedParts={collapsedParts} />
      </motion.div>
    </div>
  )
}

function BracketCorners() {
  const s: React.CSSProperties = {
    position: 'absolute',
    width: 18,
    height: 18,
    opacity: 0.25,
  }
  const line: React.CSSProperties = { position: 'absolute', background: 'var(--text-primary)' }
  type Corner = { top?: number; bottom?: number; left?: number; right?: number; t?: true; b?: true; l?: true; r?: true }
  const corners: Corner[] = [
    { top: 12, left: 12, t: true, l: true },
    { top: 12, right: 12, t: true, r: true },
    { bottom: 12, left: 12, b: true, l: true },
    { bottom: 12, right: 12, b: true, r: true },
  ]

  return (
    <>
      {corners.map((c, i) => (
        <div key={i} style={{ ...s, top: c.top, bottom: c.bottom, left: c.left, right: c.right }}>
          {'t' in c && <div style={{ ...line, top: 0, left: 0, right: 0, height: 2 }} />}
          {'b' in c && <div style={{ ...line, bottom: 0, left: 0, right: 0, height: 2 }} />}
          {'l' in c && <div style={{ ...line, top: 0, bottom: 0, left: 0, width: 2 }} />}
          {'r' in c && <div style={{ ...line, top: 0, bottom: 0, right: 0, width: 2 }} />}
        </div>
      ))}
    </>
  )
}

// Per-deck key so each deck remembers its own direction independently
function sidePrefKey(deckKey: string) { return `study-dir-${deckKey}` }

function loadDeckSidePref(deckKey: string): SideFirst | null {
  try {
    const stored = localStorage.getItem(sidePrefKey(deckKey))
    if (stored === 'term' || stored === 'definition') return stored
  } catch { /* storage unavailable: no saved preference */ }
  return null
}

function saveDeckSidePref(deckKey: string, choice: SideFirst) {
  try { localStorage.setItem(sidePrefKey(deckKey), choice) } catch { /* storage unavailable: preference not saved */ }
}

// ── Direction picker — kept for potential future use, not currently rendered ───

interface DirectionPickerProps {
  onPick: (dir: SideFirst) => void
  onBack: () => void
}

// i18n-exempt: DirectionPicker is unreachable — `phase` is only ever set to
// 'loading' | 'studying' | 'done' (see Phase's comment above), never
// 'picking-direction', so this component never renders. Kept, per the
// existing suppress-unused comment below, for potential future use; its
// copy is left untranslated because no locale will ever display it.
function DirectionPicker({ onPick, onBack }: DirectionPickerProps) {
  const optStyle = (accent: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '20px 28px',
    borderRadius: 16,
    border: accent ? '2px solid var(--accent)' : '1.5px solid var(--border)',
    background: accent ? 'var(--accent-soft)' : 'var(--bg-surface)',
    cursor: 'pointer',
    flex: 1,
    minWidth: 140,
    transition: 'border-color 0.15s, background 0.15s',
    fontFamily: 'var(--font-ui)',
  })

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 24px',
        gap: 32,
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Choose study direction {/* i18n-exempt: unreachable, see DirectionPicker comment */}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
          Your choice is saved for this deck. {/* i18n-exempt: unreachable, see DirectionPicker comment */}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', width: '100%', maxWidth: 420 }}>
        <button style={optStyle(true)} onClick={() => onPick('definition')}>
          <span style={{ fontSize: 24 }}>📖</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>Definition → Term</span> {/* i18n-exempt: unreachable, see DirectionPicker comment */}
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>See the meaning, recall the word</span> {/* i18n-exempt: unreachable, see DirectionPicker comment */}
        </button>
        <button style={optStyle(false)} onClick={() => onPick('term')}>
          <span style={{ fontSize: 24 }}>🔤</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Term → Definition</span> {/* i18n-exempt: unreachable, see DirectionPicker comment */}
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>See the word, recall the meaning</span> {/* i18n-exempt: unreachable, see DirectionPicker comment */}
        </button>
      </div>

      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14 }}
      >
        ← Go back {/* i18n-exempt: unreachable, see DirectionPicker comment */}
      </button>
    </div>
  )
}

// Suppress unused warning — DirectionPicker is kept for potential future use
void DirectionPicker

// ── Grade buttons ─────────────────────────────────────────────────────────────

interface GradeButtonsProps {
  onKnow: () => void
  onDontKnow: () => void
  // While the buffer is refilling after rapid grading there is no live card to
  // grade — the buttons are held inert until the next card is ready (§5 guard).
  disabled?: boolean
}

function GradeButtons({ onKnow, onDontKnow, disabled }: GradeButtonsProps) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const baseStyle: React.CSSProperties = {
    flex: 1,
    maxWidth: 180,
    padding: '14px 20px',
    borderRadius: 50,
    border: 'none',
    fontSize: 15,
    fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'opacity 0.2s, transform 0.1s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  }

  return (
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
      <button
        onClick={onDontKnow}
        disabled={disabled}
        style={{ ...baseStyle, background: 'var(--danger-fill)', color: 'var(--text-on-dark)' }}
        title={cs.dontKnowTitle}
      >
        {cs.dontKnow}
      </button>
      <button
        onClick={onKnow}
        disabled={disabled}
        style={{ ...baseStyle, background: 'var(--success)', color: '#fff' }}
        title={cs.knowTitle}
      >
        {cs.knowLabel}
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ClassicStudyPage() {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const { deckId, token } = useParams<{ deckId?: string; token?: string }>()
  const navigate = useNavigate()

  // Stable identifier for per-deck localStorage
  const deckKey = deckId ?? token ?? 'default'

  // Direction: initialize from localStorage for fast same-device return;
  // API preference (below) is the source of truth and reconciles on load.
  // Default is 'term' (= 'term-front') per ADR-039.
  const [sideFirst, setSideFirst] = useState<SideFirst>(() => loadDeckSidePref(deckKey) ?? 'term')
  // No direction picker phase — always go straight to loading
  const [phase, setPhase] = useState<Phase>('loading')

  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [flipped, setFlipped] = useState(false)
  // Lags behind `flipped` by half the flip animation so the front face stays
  // blanked until backfaceVisibility:hidden has hidden it (~90° rotation).
  const [committedFlip, setCommittedFlip] = useState(false)
  const blankLatchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Full deck card count — set whenever a full-deck (no card_ids) session is
  // created, never updated on subset / unknown-only sessions. Feeds the
  // "Study Again (all N)" label so it always shows the real deck size.
  const [deckCardCount, setDeckCardCount] = useState<number | null>(null)
  // "Show all" overlay (Side B full content). While open, swipe-to-grade is disabled.
  const [showAllOpen, setShowAllOpen] = useState(false)
  const [shuffle, setShuffle] = useState(false)
  const [progress, setProgress] = useState<ClassicSessionProgress | null>(null)
  const [showSettingsOpen, setShowSettingsOpen] = useState(false)

  // Back-gesture (Android TWA): when the panel opens we push a fake history entry
  // so the OS back gesture closes the panel instead of the deck.
  const handleOpenPanel = useCallback(() => {
    window.history.pushState({ cspanel: true }, '', window.location.href)
    setShowSettingsOpen(true)
  }, [])

  const handleClosePanel = useCallback(() => {
    setShowSettingsOpen(false)
    // Only pop if this history entry is the one we pushed (avoids double-popping).
    if (window.history.state?.cspanel) window.history.back()
  }, [])

  useEffect(() => {
    if (!showSettingsOpen) return
    const handler = () => setShowSettingsOpen(false)
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [showSettingsOpen])

  // Track unknown card ids for "Study Unknown Only"
  const unknownCardIds = useRef<Set<string>>(new Set())

  // Display preference (ADR-039) — authenticated deck pages only.
  // Fires in parallel with session creation; result reconciles sideFirst and
  // provides collapsed_parts without blocking the gesture pipeline.
  const { data: deckPref } = useDeckDisplayPreference(deckId)
  const upsertPref = useUpsertDeckDisplayPreference()

  // Sync sideFirst from API when the preference loads (handles cross-device;
  // no-ops when localStorage already matches). Runs only for deck pages.
  useEffect(() => {
    if (!deckPref) return
    setSideFirst(deckPref.front_side === 'definition-front' ? 'definition' : 'term')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckPref?.front_side])

  // Collapsed parts for the group face: from stored preference, or null (= first
  // available part, the ADR-037 wave-1 fallback). Undefined until pref loads.
  const collapsedParts = deckPref?.collapsed_parts ?? null

  const createSession = useCreateClassicSession()
  const toggleShuffle = useToggleClassicShuffle()
  const completeSession = useCompleteClassicSession()

  // Counters update optimistically at grade time (see performGrade) so they move
  // in lockstep with the instant card advance. The grade POST returns the
  // authoritative progress a full RTT later — we reconcile to it, but never let a
  // stale snapshot (fewer grades committed than we've already counted during
  // rapid grading) drag the tally backwards.
  const reconcileProgress = useCallback((server: ClassicSessionProgress) => {
    setProgress((local) => {
      if (!local) return server
      return server.reviewed >= local.reviewed ? server : local
    })
  }, [])

  // Optimistic one-ahead buffer + serial background grade poster (ADR-038).
  const buffer = useClassicStudyBuffer(sessionId, { onProgress: reconcileProgress })

  // Frozen card: while a swiped card flies off (≈320 ms), keep displaying it so the
  // fly-off animation plays out, even though the buffer has already advanced
  // logically. The buffered next card is shown the moment the fly-off completes —
  // a fixed local animation, never a network wait. Button/keyboard grades skip the
  // freeze entirely and swap instantly.
  const [frozenCard, setFrozenCard] = useState<ClassicNextCard | null>(null)
  const frozenRef = useRef(false)
  const flyoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Last live card shown — the §5 underrun guard. The depth-1 buffer empties for
  // one RTT when the user grades faster than the poster refills. Rather than fall
  // back to a blank "Loading card…" flash, we HOLD the last card frozen (grading
  // disabled) until the real next card is ready. Reset on (re)create so a new
  // session never holds the previous session's last card.
  const lastCardRef = useRef<ClassicNextCard | null>(null)
  useEffect(() => {
    if (buffer.current) lastCardRef.current = buffer.current
    // buffer.current is the live look-ahead head; re-run whenever the shown card
    // changes so the held card always mirrors the last real card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer.current])

  // Underrun hold: mid-session the buffer momentarily has no current card while
  // the poster refills. Show the last card — never blank, never a wrong NEXT
  // card. Excluded for the genuine initial/shuffle load (buffer.loading) and for
  // completion (buffer.done), where holding the old card would be wrong.
  const underrunHold =
    !buffer.current && !buffer.loading && !buffer.done && lastCardRef.current
      ? lastCardRef.current
      : null

  // The card actually rendered: the frozen (flying-off) card if any, else the
  // live buffer head, else the underrun-held card. Null → genuine initial load.
  const nextCard = frozenCard ?? buffer.current ?? underrunHold

  // True while we are only HOLDING the last card (no live current to grade) —
  // grade input is blocked until the buffer refills.
  const gradingHeld = !frozenCard && !buffer.current && !!underrunHold

  // ── Session creation helper (used by init, study-again) ───────────────────

  const doCreateSession = useCallback((body: Parameters<typeof createSession.mutate>[0]) => {
    setSessionError(null)
    setFlipped(false)
    lastCardRef.current = null
    setPhase('loading')
    createSession.mutate(body, {
      onSuccess: (session) => {
        setSessionId(session.id)
        setProgress(session.progress)
        setShuffle(session.shuffle)
        setPhase('studying')
        // Only a full-deck session (no card_ids filter) reflects the real deck size.
        if (!body.card_ids?.length) {
          setDeckCardCount(session.progress.total)
        }
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: { detail?: string } | string } } })
          ?.response?.data?.detail
        const text = typeof msg === 'object' ? msg?.detail : msg
        setSessionError(text ?? cs.loadingSession)
      },
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Init: create session on mount ─────────────────────────────────────────

  const sessionBody = deckId ? { deck_id: deckId, shuffle: false } : { share_token: token, shuffle: false }

  useEffect(() => {
    doCreateSession(sessionBody)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Swap direction mid-session ────────────────────────────────────────────
  // key prop on FlipCard (see render below) forces remount on direction change,
  // resetting internal state cleanly. Persistence: API for authenticated deck
  // pages (fire-and-forget — never blocks grading), localStorage for shared/anon.

  const handleSwapDirection = () => {
    stopActiveAudio()
    const next: SideFirst = sideFirst === 'definition' ? 'term' : 'definition'
    setSideFirst(next)
    setFlipped(false)
    setShowAllOpen(false)
    if (deckId) {
      // Write to API in the background — no await, no effect on the gesture pipeline
      upsertPref.mutate({
        deckId,
        front_side: next === 'definition' ? 'definition-front' : 'term-front',
        collapsed_parts: collapsedParts,
      })
      // Keep localStorage in sync so a hard-reload before the API responds
      // still shows the correct direction on next mount
      saveDeckSidePref(deckKey, next)
    } else {
      saveDeckSidePref(deckKey, next)
    }
  }

  // ── Detect session done when the buffer is exhausted ──────────────────────
  // Gated on !frozenCard so a final swipe's fly-off plays out before the
  // complete screen replaces the card.

  useEffect(() => {
    if (phase !== 'studying') return
    if (buffer.done && !frozenCard) {
      setPhase('done')
    }
  }, [buffer.done, frozenCard, phase])

  // ── Grade ──────────────────────────────────────────────────────────────────
  //
  // performGrade does the logical grade (instant buffer advance + background
  // post) with NO animation — used by buttons and keyboard. The swipe path wraps
  // it with the fly-off/freeze below. Returns true if a grade actually fired.

  const performGrade = useCallback(
    (rating: 'know' | 'dont_know'): boolean => {
      if (frozenRef.current || buffer.error) return false
      const cur = buffer.current
      if (!cur) return false
      if (rating === 'dont_know') {
        unknownCardIds.current.add(cur.card_id)
      }
      const gradedId = buffer.grade(rating)
      if (!gradedId) return false
      stopActiveAudio()
      // Optimistic counter bump — instant, in lockstep with the card advance.
      // The grade POST's authoritative progress reconciles via reconcileProgress.
      setProgress((p) =>
        p
          ? {
              ...p,
              reviewed: p.reviewed + 1,
              known: rating === 'know' ? p.known + 1 : p.known,
              unknown: rating === 'dont_know' ? p.unknown + 1 : p.unknown,
              remaining: Math.max(0, p.remaining - 1),
            }
          : p
      )
      setFlipped(false)
      setShowAllOpen(false)
      return true
    },
    [buffer]
  )

  const handleGrade = useCallback(
    (rating: 'know' | 'dont_know') => {
      // Button / keyboard path: instant swap, no fly-off.
      performGrade(rating)
      setSwipeDx(0)
    },
    [performGrade]
  )

  // ── Swipe-to-grade (mobile / touch only) ────────────────────────────────────

  const prefersReducedMotion = useReducedMotion()

  // Duration until the front face is hidden behind the 90° edge during a forward flip.
  // The flip animation is 450 ms; this must exceed the time to reach rotateY(90°).
  const FLIP_BLANK_HOLD_MS = 240

  useEffect(() => {
    if (blankLatchRef.current) {
      clearTimeout(blankLatchRef.current)
      blankLatchRef.current = null
    }
    if (flipped) {
      // Delay releasing the blank until the front face has rotated past 90°.
      const delay = prefersReducedMotion ? 0 : FLIP_BLANK_HOLD_MS
      blankLatchRef.current = setTimeout(() => {
        setCommittedFlip(true)
        blankLatchRef.current = null
      }, delay)
    } else {
      // Flipping back: the blank must be ready before the front face reappears,
      // so we restore it immediately (it re-appears in the second half).
      setCommittedFlip(false)
    }
    return () => {
      if (blankLatchRef.current) {
        clearTimeout(blankLatchRef.current)
        blankLatchRef.current = null
      }
    }
  }, [flipped, prefersReducedMotion])

  const [swipeDx, setSwipeDx] = useState(0)
  // Drives the CSS transition: true = no transition (live drag), false = animated snap/exit
  const [swipeDragging, setSwipeDragging] = useState(false)
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null)
  const swipeLockedH = useRef(false)
  const didSwipeRef = useRef(false) // prevents tap-to-flip firing after a real swipe

  // prevCardIdRef tracks the last card we rendered. When nextCard.card_id changes, a
  // useLayoutEffect resets swipeDx to 0 synchronously before the browser paints — this
  // prevents the incoming card from inheriting the outgoing card's exit transform.
  const prevCardIdRef = useRef<string | null>(null)
  // skipTransition is set alongside setSwipeDx(0) in the card-change useLayoutEffect so
  // that the transition property is also 'none' in the same synchronous batch. Without
  // this, the CSS engine would animate the position reset (off-screen → 0) even though
  // that intermediate off-screen state was never painted. Cleared by a useEffect after paint.
  const [skipTransition, setSkipTransition] = useState(false)
  // Grade cue: brief outcome label shown after a committed swipe
  const [gradeCue, setGradeCue] = useState<'know' | 'dont_know' | null>(null)
  const cueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') return          // desktop: no swipe
    if (showAllOpen) return                         // overlay open: swipe disabled (ADR-037)
    if (e.clientX < IOS_EDGE_PX) return            // iOS left-edge guard
    if (!nextCard) return                           // no card on screen yet
    swipeStart.current = { x: e.clientX, y: e.clientY, t: Date.now() }
    swipeLockedH.current = false
    didSwipeRef.current = false
  }, [nextCard, showAllOpen])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!swipeStart.current) return
    const dx = e.clientX - swipeStart.current.x
    const dy = e.clientY - swipeStart.current.y

    if (!swipeLockedH.current) {
      const absDx = Math.abs(dx)
      const absDy = Math.abs(dy)
      // Wait until we have enough displacement to read direction reliably.
      if (absDx < SWIPE_LOCK_PX && absDy < SWIPE_LOCK_PX) return
      // Still more vertical than horizontal at this sample — keep tracking.
      // A natural thumb arc often starts diagonal; nulling here permanently kills
      // the gesture before the finger has a chance to go sideways (symptom 1b).
      // With touchAction:'none' there is no scroll to protect, so we can wait.
      if (absDy >= absDx) return
      // Horizontal motion is dominant — lock in
      swipeLockedH.current = true
      setSwipeDragging(true)
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    if (swipeLockedH.current) {
      didSwipeRef.current = true
      setSwipeDx(dx)
    }
  }, [])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!swipeStart.current || !swipeLockedH.current) {
      swipeStart.current = null
      swipeLockedH.current = false
      return
    }

    const dx = e.clientX - swipeStart.current.x
    const dt = Math.max(Date.now() - swipeStart.current.t, 1)
    const vel = Math.abs(dx) / dt

    swipeStart.current = null
    swipeLockedH.current = false
    setSwipeDragging(false) // re-enable transition for snap/exit

    const commit = Math.abs(dx) >= SWIPE_COMMIT_PX || vel >= SWIPE_COMMIT_VEL

    if (commit) {
      const rating: 'know' | 'dont_know' = dx > 0 ? 'know' : 'dont_know'
      const cardOnScreen = buffer.current
      // Advance the buffer + fire the background grade immediately (no network wait).
      if (!performGrade(rating)) {
        setSwipeDx(0)
        return
      }
      // Brief outcome cue — shown in both motion paths
      setGradeCue(rating)
      if (cueTimerRef.current) clearTimeout(cueTimerRef.current)
      cueTimerRef.current = setTimeout(() => setGradeCue(null), 650)
      if (prefersReducedMotion) {
        setSwipeDx(0) // instant swap to the buffered card, no fly-off
      } else {
        // Freeze the graded card on screen so its fly-off plays out, then swap to
        // the already-advanced buffered card. The fly-off is a fixed local
        // animation; the new card never waits on the network.
        setFrozenCard(cardOnScreen)
        frozenRef.current = true
        setSwipeDx(dx > 0 ? window.innerWidth * 1.2 : -window.innerWidth * 1.2)
        if (flyoffTimerRef.current) clearTimeout(flyoffTimerRef.current)
        flyoffTimerRef.current = setTimeout(() => {
          frozenRef.current = false
          // Clearing the frozen card swaps display to buffer.current; the
          // card-change useLayoutEffect then resets swipeDx → 0 before paint.
          setFrozenCard(null)
        }, FLYOFF_MS)
      }
    } else {
      setSwipeDx(0) // CSS transition snaps back
    }
  }, [prefersReducedMotion, performGrade, buffer])

  const handlePointerCancel = useCallback(() => {
    swipeStart.current = null
    swipeLockedH.current = false
    setSwipeDragging(false)
    setSwipeDx(0)
  }, [])

  // ── Card-change swipeDx reset ─────────────────────────────────────────────
  // Runs synchronously after DOM commit but before the browser paints.
  // When nextCard changes (new card arrived), swipeDx resets to 0 in the same
  // paint frame, so the incoming card is never rendered off-screen then
  // animated back — which read as a "return" of the graded card.
  useLayoutEffect(() => {
    if (nextCard?.card_id && nextCard.card_id !== prevCardIdRef.current) {
      prevCardIdRef.current = nextCard.card_id
      stopActiveAudio()
      // Belt-and-suspenders: if setFlipped(false) and the nextCard update land
      // in separate render passes (TanStack Query notifyManager timing), the new
      // card would momentarily mount flipped and paint its back side before the
      // second render corrects it. Resetting here before paint closes that window.
      setFlipped(false)
      // Cancel any pending blank-latch and reset committedFlip here (before paint)
      // so the new card's front face is always blanked on its first rendered frame.
      if (blankLatchRef.current) {
        clearTimeout(blankLatchRef.current)
        blankLatchRef.current = null
      }
      setCommittedFlip(false)
      // Existing swipe-path reset (inert on desktop but kept for mobile).
      setSkipTransition(true)
      setSwipeDx(0)
    }
  }, [nextCard?.card_id])

  // Re-enable transition after the instant reset has been painted.
  // useEffect runs after paint, so the new card was already displayed at centre
  // before the transition is re-armed for the next drag on this card.
  useEffect(() => {
    if (skipTransition) setSkipTransition(false)
  }, [skipTransition])

  useEffect(() => () => {
    if (cueTimerRef.current) clearTimeout(cueTimerRef.current)
    if (flyoffTimerRef.current) clearTimeout(flyoffTimerRef.current)
    if (blankLatchRef.current) clearTimeout(blankLatchRef.current)
  }, [])

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (phase !== 'studying') return
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      // Overlay open: Escape closes it; all other study keys are suppressed.
      if (showSettingsOpen) {
        if (e.key === 'Escape') setShowSettingsOpen(false)
        return
      }
      if (showAllOpen) {
        if (e.key === 'Escape') setShowAllOpen(false)
        return
      }
      if (e.key === ' ') {
        e.preventDefault()
        setFlipped((f) => !f)
      } else if (e.key === 'ArrowRight') {
        handleGrade('know')
      } else if (e.key === 'ArrowLeft') {
        handleGrade('dont_know')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [phase, handleGrade, showAllOpen, showSettingsOpen])

  // ── Shuffle toggle ────────────────────────────────────────────────────────

  const handleToggleShuffle = () => {
    if (!sessionId || buffer.shuffling) return
    toggleShuffle.mutate(sessionId, {
      onSuccess: (session) => {
        setShuffle(session.shuffle)
        // The server reordered the unreviewed queue → our one-ahead buffer is now
        // stale (the ADR-038 static-queue assumption holds only within an
        // un-shuffled session). Drain the poster and refetch the fresh order.
        buffer.invalidateForShuffle()
      },
    })
  }

  // ── Study Again ───────────────────────────────────────────────────────────

  const handleStudyAgain = () => {
    unknownCardIds.current.clear()
    const body = deckId
      ? { deck_id: deckId, shuffle }
      : { share_token: token, shuffle }
    doCreateSession(body)
  }

  // ── Study Unknown Only ────────────────────────────────────────────────────

  const handleStudyUnknown = () => {
    const ids = [...unknownCardIds.current]
    unknownCardIds.current.clear()
    const body = deckId
      ? { deck_id: deckId, shuffle, card_ids: ids }
      : { share_token: token, shuffle, card_ids: ids }
    doCreateSession(body)
  }

  // ── Back ──────────────────────────────────────────────────────────────────

  const handleBack = () => {
    if (deckId) {
      navigate(`/decks/${deckId}`)
    } else {
      navigate(-1)
    }
  }

  // ── Complete on unmount if still active ──────────────────────────────────

  useEffect(() => {
    return () => {
      if (sessionId) {
        completeSession.mutate(sessionId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // ── Render ────────────────────────────────────────────────────────────────

  // Full Side B stack — fed to the "Show all" overlay.
  // Computed from nextCard only when available; FlipCard resolves its own sides internally.
  // Every Side-B part as stored, never the Term (ADR-104 Decision 4).
  const groupParts = nextCard ? allSideBParts(nextCard) : []

  // The direction this card is shown in: the reader's, unless its front would
  // have nothing to show (ADR-104 Decision 7). `sideFirst` itself never changes.
  const cardSideFirst = nextCard ? effectiveSideFirst(nextCard, sideFirst) : sideFirst

  // Blank the term in Example sides when: definition-first direction AND card not yet flipped.
  // After flip (or in term-first mode), the term is already visible, so no blanking needed.
  // A blank term has nothing to hide, so it blanks nothing.
  const termText = nextCard ? filledTermText(nextCard) : null
  // committedFlip (not flipped) gates this so the front face stays blanked for
  // the entire first half of the flip animation, until backfaceVisibility hides it.
  const blankTerm = cardSideFirst === 'definition' && !committedFlip ? termText : null

  if (phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '0 24px' }}>
        {sessionError ? (
          <>
            <div style={{ fontSize: 15, color: 'var(--danger)', textAlign: 'center' }}>{sessionError}</div>
            <button
              onClick={() => doCreateSession(sessionBody)}
              style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}
            >
              {cs.retry}
            </button>
            <button
              onClick={handleBack}
              style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' }}
            >
              {cs.goBack}
            </button>
          </>
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: 15 }}>{cs.loadingSession}</div>
        )}
      </div>
    )
  }

  if (phase === 'done' && progress) {
    return (
      <StudyComplete
        progress={progress}
        hasUnknown={progress.unknown > 0}
        deckCardCount={deckCardCount ?? progress.total}
        onStudyAgain={handleStudyAgain}
        onStudyUnknown={handleStudyUnknown}
        onBack={handleBack}
      />
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 16px',
        // Prevent rubber-band / native vertical scroll starting anywhere on the
        // page (including the flex gap between card, counter, and buttons).
        // touch-action is NOT inherited, so fixed-position overlay scroll
        // containers (ShowAllOverlay, DisplaySettingsPanel) are unaffected.
        overscrollBehavior: 'none',
        touchAction: 'none',
      }}
    >
      {/* Background-grade failure banner — surfaced, never silently dropped.
          Grading is blocked until the user retries (or the session is abandoned). */}
      {buffer.error && (
        <div
          role="alert"
          style={{
            width: '100%',
            maxWidth: 640,
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'var(--danger-soft)',
            border: '1px solid var(--danger)',
            color: 'var(--danger)',
            fontSize: 13,
            fontFamily: 'var(--font-ui)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>{buffer.error}</span>
          <button
            onClick={() => buffer.retry()}
            style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 36 }}
          >
            {cs.retry}
          </button>
        </div>
      )}

      {/* Top bar */}
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 0',
        }}
      >
        <button
          onClick={handleBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: '12px 8px', minHeight: 44 }}
        >
          {cs.back}
        </button>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Direction indicator + swap button */}
          <button
            onClick={handleSwapDirection}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: 13,
              padding: '6px 12px',
              transition: 'background 0.2s',
              minHeight: 36,
            }}
            title={cs.swapDirection}
          >
            <ArrowLeftRight size={14} />
            {sideFirst === 'definition' ? cs.defFirst : cs.termFirst}
          </button>

          <button
            onClick={handleToggleShuffle}
            style={{
              background: shuffle ? 'var(--accent-fill)' : 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              color: shuffle ? 'var(--text-on-accent)' : 'var(--text-secondary)',
              fontSize: 14,
              padding: '6px 14px',
              transition: 'background 0.2s',
            }}
            title={cs.shuffle}
          >
            {cs.shuffle}
          </button>

          {/* Display settings — authenticated deck pages only */}
          {deckId && (
            <button
              onClick={handleOpenPanel}
              aria-label={cs.cardDisplay}
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px 10px',
                minHeight: 36,
                minWidth: 36,
              }}
              title={cs.cardDisplay}
            >
              <Settings size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {progress && (
        <div style={{ width: '100%', maxWidth: 640, marginBottom: 24 }}>
          <StudyProgress progress={progress} />
        </div>
      )}

      {/* Card area — flex layout keeps buttons anchored below the fixed-height card */}
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: 40,
        }}
      >
        {nextCard ? (
          <>
            {/*
              Swipe wrapper: handles touch gestures (mobile only).
              The translateX is applied only to the inner div so the
              counter/buttons/hint below are never shifted.
              overflow is intentionally NOT set here — overflow:hidden would clip
              the FlipCard's 3D perspective projection and drop shadow (ADR-012
              lesson: never let overflow:hidden double as a card content-fitter).
              The tint overlay below carries its own borderRadius to stay masked
              to the rounded card shape without needing a parent clip.
            */}
            <div
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              style={{ width: '100%', maxWidth: 640, position: 'relative', touchAction: 'none' }}
            >
              <div
                style={{
                  transform: `translateX(${swipeDx}px) rotate(${swipeDx * 0.025}deg)`,
                  transformOrigin: 'center bottom',
                  transition: (swipeDragging || skipTransition)
                    ? 'none'
                    : `transform ${prefersReducedMotion ? '0s' : '0.3s'} cubic-bezier(0.25,0.46,0.45,0.94)`,
                  borderRadius: 16,
                }}
              >
                {/*
                  AnimatePresence with mode="wait" serializes the per-card
                  unmount→mount so the new FlipCard's flip motion.div mounts in
                  a clean, isolated presence context — it can never inherit the
                  outgoing card's MotionValue or mount while the prior flip
                  animation is still running (the residual ~1/10 slide-in race).
                  initial={false} suppresses any presence-level enter animation;
                  FlipCard is not a motion child, so there is NO FlipCard-level
                  enter/exit motion — the mobile fly-off stays the swipe
                  wrapper's job, and tap-to-flip stays the inner motion.div's.
                  key includes sideFirst so a direction swap also remounts and
                  resets internal state cleanly.
                */}
                <AnimatePresence mode="wait" initial={false}>
                  <FlipCard
                    key={`${nextCard.card_id}-${sideFirst}`}
                    card={nextCard}
                    sideFirst={cardSideFirst}
                    flipped={flipped}
                    collapsedParts={collapsedParts}
                    blankTerm={blankTerm}
                    onShowAll={() => setShowAllOpen(true)}
                    onFlip={() => {
                      // Block flip if the pointer moved enough to count as a swipe
                      if (didSwipeRef.current) {
                        setTimeout(() => { didSwipeRef.current = false }, 0)
                        return
                      }
                      setFlipped((f) => !f)
                    }}
                  />
                </AnimatePresence>

                {/* Tint overlay — covers the card face with directional color.
                    Know = success green; Don't know = warning amber (not --danger,
                    which is reserved for in-the-moment errors per DESIGN_SYSTEM §3). */}
                {swipeDx !== 0 && (() => {
                  const opacity = Math.min(Math.abs(swipeDx) / SWIPE_COMMIT_PX, 1)
                  const isKnow = swipeDx > 0
                  return (
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        // borderRadius clips this div's own background paint to the
                        // card's rounded shape — no parent overflow:hidden needed.
                        borderRadius: 16,
                        backgroundColor: isKnow ? 'rgba(39,174,96,0.22)' : 'rgba(243,156,18,0.22)',
                        opacity,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isKnow ? 'flex-start' : 'flex-end',
                        padding: '0 28px',
                      }}
                    >
                      {opacity > 0.12 && (
                        <span style={{
                          fontSize: 18,
                          fontWeight: 800,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: isKnow ? 'var(--success)' : 'var(--warning)',
                          opacity: Math.min(opacity * 2.5, 1),
                          fontFamily: 'var(--font-ui)',
                        }}>
                          {isKnow ? cs.know : cs.dontKnow}
                        </span>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* Grade cue — transient Know / Don't know label after a swipe commit.
                Idle state is empty: the canonical remaining count lives in
                StudyProgress above the card; showing it twice was a duplicate. */}
            <div
              style={{
                fontSize: 13,
                fontFamily: 'var(--font-ui)',
                fontWeight: gradeCue ? 700 : 400,
                letterSpacing: gradeCue ? '0.05em' : 'normal',
                textTransform: gradeCue ? 'uppercase' : 'none',
                color: gradeCue === 'know'
                  ? 'var(--success)'
                  : gradeCue === 'dont_know'
                  ? 'var(--warning)'
                  : 'var(--text-secondary)',
                transition: 'color 0.15s',
                // Reserve line height even when empty so layout doesn't jump
                minHeight: '1em',
              }}
            >
              {gradeCue === 'know'
                ? cs.know
                : gradeCue === 'dont_know'
                ? cs.dontKnow
                : null}
            </div>

            <GradeButtons onKnow={() => handleGrade('know')} onDontKnow={() => handleGrade('dont_know')} disabled={gradingHeld} />

            {/* Keyboard hint — hidden on touch devices and when card is flipped */}
            <div
              className="classic-kb-hint"
              data-hidden={flipped ? 'true' : 'false'}
              style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            >
              {cs.kbHint}
            </div>
            {/* Swipe hint — visible only on touch devices, hidden after first drag */}
            <div
              className="classic-swipe-hint"
              aria-hidden
              style={{ fontSize: 12, color: 'var(--text-secondary)', opacity: swipeDx !== 0 ? 0 : 0.7 }}
            >
              {cs.swipeHint}
            </div>
          </>
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: 15 }}>{cs.loadingCard}</div>
        )}
      </div>

      {/* "Show all" overlay — every Side B part, the only scroll surface (ADR-037) */}
      {showAllOpen && (
        <ShowAllOverlay parts={groupParts} onClose={() => { stopActiveAudio(); setShowAllOpen(false) }} blankTerm={blankTerm} />
      )}

      {/* Display settings panel — authenticated deck pages only (ADR-039).
          Rendered whenever deckId is present — deckPref may be undefined while
          the preference fetch is still in flight or errored; the panel defaults
          to term-front / no collapsed parts in that case so the gear is never
          a no-op. */}
      {showSettingsOpen && deckId && (
        <DisplaySettingsPanel
          deckId={deckId}
          currentParts={deckPref?.collapsed_parts ?? null}
          currentCard={nextCard ?? null}
          saving={upsertPref.isPending}
          onSave={(parts) => {
            const frontSide = deckPref?.front_side ?? (sideFirst === 'definition' ? 'definition-front' : 'term-front')
            upsertPref.mutate(
              { deckId, front_side: frontSide, collapsed_parts: parts },
              { onSuccess: handleClosePanel },
            )
          }}
          onClose={handleClosePanel}
        />
      )}
    </div>
  )
}
